import { CommunityId, emailTokens, ObservationKind } from "@sp-crm/core";
import { MandatoryEmailToken } from "components/messages/mandatory-token";
import { MessageComposePredeterminedRecipient } from "components/messages/message-compose-predetermined-recipient";
import { PlacementMessageCompose } from "components/messages/types";
import { fancyAlert } from "components/ui/fancy-alert";
import { InlineBanner } from "components/ui/inline-banner";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    AdvancedSearchEntityType,
    useRequestCommunityListingUpdateMutation,
} from "generated/graphql";
import React, { MutableRefObject, useCallback } from "react";
import { useCanRequestCommunityAvailability } from "store/selectors/hooks";

interface Props {
    communityId: CommunityId;
    communityName: string;
    subject: string;
    body: string;
    observationFocus: ObservationKind;
    onDismiss: () => void;
    onSuccess: () => void;
}

export const CommunityRequestListingUpdatePanel: React.FC<Props> = props => {
    const canRequestAvailability = useCanRequestCommunityAvailability();
    const {
        communityId,
        communityName,
        onDismiss,
        onSuccess,
        body,
        subject,
        observationFocus,
    } = props;
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const inlineEditorRef =
        React.useRef<PlacementMessageCompose>() as MutableRefObject<PlacementMessageCompose>;
    const requestCommunityListingUpdate = useRequestCommunityListingUpdateMutation();

    const handleSend = useCallback(async () => {
        const message = inlineEditorRef.current.getMessage();
        try {
            if (!(message.body || "").includes(emailTokens.liveLink)) {
                const errorMessage = `To send the form the message body must contain ${emailTokens.liveLink}. To proceed, add ${emailTokens.liveLink} to the body and send again.`;
                setErrorMessage(errorMessage);
                await fancyAlert("Link Placeholder Missing", errorMessage);
                return;
            }
            setErrorMessage(null);

            await requestCommunityListingUpdate.mutateAsync({
                communityId,
                message: {
                    entityId: message.entityId,
                    subject: message.subject,
                    body: message.body,
                    attachmentIds: message.attachmentIds,
                },
                observationFocus,
            });
            onSuccess();
        } catch (e) {
            setErrorMessage(e.message);
        }
    }, [
        communityId,
        inlineEditorRef,
        requestCommunityListingUpdate,
        onSuccess,
        observationFocus,
    ]);

    if (!canRequestAvailability) {
        return null;
    }

    return (
        <ResponsivePanel
            type={PanelType.extraLarge}
            onDismiss={onDismiss}
            isOpen={true}
            headerText="Request availability update">
            <div className="space-y-4">
                {communityName ? (
                    <div className="flex space-x-2 items-center">
                        <p>To:</p>
                        <p>
                            <strong>{communityName || "the community"}</strong>
                        </p>
                    </div>
                ) : null}
                <p>
                    Sends a message with a link the community can use to update their
                    availability.
                </p>
                <MessageComposePredeterminedRecipient
                    entityId={communityId}
                    entityType={AdvancedSearchEntityType.Community}
                    onDismiss={onDismiss}
                    ref={inlineEditorRef}
                    requestSpecialMessageExperience={
                        ObservationKind.Availability
                            ? "request-availability-update"
                            : "request-pricing-update"
                    }
                    initialBody={body}
                    initialSubject={subject}
                />
                <div className="my-1">
                    <MandatoryEmailToken
                        use={
                            observationFocus === ObservationKind.Availability
                                ? "request-availability-update"
                                : "request-pricing-update"
                        }
                    />
                </div>
                <div className="flex items-center justify-end space-x-2">
                    <SecondaryButton
                        type="button"
                        onClick={onDismiss}
                        disabled={requestCommunityListingUpdate.isLoading}>
                        Discard
                    </SecondaryButton>
                    <PrimaryButton
                        type="button"
                        onClick={handleSend}
                        disabled={requestCommunityListingUpdate.isLoading}>
                        {requestCommunityListingUpdate.isLoading ? "Sending..." : "Send"}
                    </PrimaryButton>
                </div>
                {errorMessage ? (
                    <InlineBanner type="error">{errorMessage}</InlineBanner>
                ) : null}
            </div>
        </ResponsivePanel>
    );
};
